/**
 * 找回密码页（公开流程，不依赖登录态）。
 *
 * 两步：
 * 1. 输入邮箱 → 发重置验证码（后端不暴露邮箱是否已注册，一律提示"若已注册会收到"）；
 * 2. 输入验证码 + 新密码 + 确认 → 重置成功后跳登录页（旧会话已全部失效）。
 */
import { useEffect, useState } from 'react';
import type { FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { authApi } from '../api/auth.js';
import { isApiError, toUserMessage } from '../api/errors.js';
import { Button } from '../components/Button.js';
import { Card } from '../components/Card.js';
import { toast } from '../store/toast.js';

const RESEND_COUNTDOWN_SECONDS = 60;

export function ForgotPasswordPage() {
  const [step, setStep] = useState<'email' | 'reset'>('email');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [countdown, setCountdown] = useState(0);

  const navigate = useNavigate();

  useEffect(() => {
    if (countdown <= 0) return;
    const timer = window.setTimeout(() => setCountdown((value) => (value > 0 ? value - 1 : 0)), 1000);
    return () => window.clearTimeout(timer);
  }, [countdown]);

  const normalizedEmail = email.trim().toLowerCase();

  async function sendCode(): Promise<void> {
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(normalizedEmail)) {
      setError('请输入正确的邮箱地址');
      return;
    }
    setError(null);
    setBusy(true);
    try {
      await authApi.forgotPassword(normalizedEmail);
      setStep('reset');
      setCountdown(RESEND_COUNTDOWN_SECONDS);
      toast.success('验证码已发送', `若 ${normalizedEmail} 已注册，你会收到一封重置密码邮件（不在收件箱请检查垃圾邮件）`);
    } catch (caught) {
      setError(toUserMessage(caught));
    } finally {
      setBusy(false);
    }
  }

  async function handleReset(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    if (code.length !== 6) {
      setError('请输入 6 位邮箱验证码');
      return;
    }
    if (password.length < 8 || password.length > 72) {
      setError('新密码至少 8 位（上限 72 位）');
      return;
    }
    if (password !== confirm) {
      setError('两次输入的新密码不一致');
      return;
    }
    setError(null);
    setBusy(true);
    try {
      await authApi.resetPassword(normalizedEmail, code, password);
      toast.success('密码已重置', '请用新密码登录（所有设备已退出，需重新登录）');
      navigate('/login', { replace: true });
    } catch (caught) {
      setError(
        isApiError(caught) && caught.code === 'UNAUTHORIZED'
          ? '验证码无效或已过期，请重新获取后再试'
          : toUserMessage(caught),
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="auth-shell">
      <Card
        className="auth-card"
        title={step === 'email' ? '找回密码' : '设置新密码'}
        subtitle={
          step === 'email'
            ? '输入注册邮箱，我们会发送 6 位重置验证码。'
            : `验证码已发送至 ${normalizedEmail}，请查收邮件并设置新密码。`
        }
      >
        {step === 'email' ? (
          <form
            className="stack"
            onSubmit={(event) => {
              event.preventDefault();
              void sendCode();
            }}
            noValidate
          >
            {error !== null ? (
              <div className="banner banner-danger" role="alert">
                <span className="banner-ico" aria-hidden="true">
                  !
                </span>
                <span className="wrap-anywhere">{error}</span>
              </div>
            ) : null}

            <div className="field">
              <label className="field-label" htmlFor="forgot-email">
                注册邮箱
              </label>
              <input
                id="forgot-email"
                className="input"
                type="email"
                value={email}
                autoComplete="email"
                placeholder="you@example.com"
                onChange={(event) => setEmail(event.target.value)}
              />
            </div>

            <Button type="submit" variant="primary" className="btn-block" loading={busy}>
              发送验证码
            </Button>

            <p className="auth-foot">
              想起来了？<Link to="/login">返回登录</Link>
            </p>
          </form>
        ) : (
          <form className="stack" onSubmit={handleReset} noValidate>
            {error !== null ? (
              <div className="banner banner-danger" role="alert">
                <span className="banner-ico" aria-hidden="true">
                  !
                </span>
                <span className="wrap-anywhere">{error}</span>
              </div>
            ) : null}

            <div className="field">
              <label className="field-label" htmlFor="reset-code">
                邮箱验证码
              </label>
              <input
                id="reset-code"
                className="input input-mono"
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={6}
                value={code}
                placeholder="6 位数字"
                onChange={(event) => setCode(event.target.value.replace(/\D/g, '').slice(0, 6))}
              />
            </div>

            <div className="field">
              <label className="field-label" htmlFor="reset-password">
                新密码
              </label>
              <input
                id="reset-password"
                className="input"
                type="password"
                value={password}
                autoComplete="new-password"
                placeholder="至少 8 位"
                onChange={(event) => setPassword(event.target.value)}
              />
            </div>

            <div className="field">
              <label className="field-label" htmlFor="reset-confirm">
                确认新密码
              </label>
              <input
                id="reset-confirm"
                className="input"
                type="password"
                value={confirm}
                autoComplete="new-password"
                placeholder="再输入一次"
                onChange={(event) => setConfirm(event.target.value)}
              />
            </div>

            <div className="verify-actions">
              <Button
                type="button"
                variant="ghost"
                onClick={() => {
                  void sendCode();
                }}
                disabled={busy || countdown > 0}
              >
                {countdown > 0 ? `重新发送(${countdown}s)` : '重新发送验证码'}
              </Button>
              <Button type="submit" variant="primary" loading={busy} disabled={code.length !== 6}>
                重置密码
              </Button>
            </div>

            <p className="auth-foot">
              <Link to="/login">返回登录</Link>
            </p>
          </form>
        )}
      </Card>
    </div>
  );
}
